// ---------------------------------------------------------------------------
// cameraStash — durable holding pen for InAppCamera shots (estimator app).
//
// Shots used to live only in component state: kill the PWA (or crash, or
// let iOS reap the tab) before tapping "Use N Photos" and every capture
// was gone. This stashes each shot's JPEG blob + tags in IndexedDB the
// moment it's taken, keyed by client, and the camera restores them on
// reopen. Cleared when the shots are handed to the upload pipeline or
// explicitly discarded.
//
// Own DB (not estimatorQueue's) so neither store needs a version dance
// when the other evolves. Degrades gracefully: no IndexedDB → stash calls
// no-op and the camera behaves exactly like the RAM-only v1.
// ---------------------------------------------------------------------------

const DB_NAME = 'bbm_camera_shots';
const STORE = 'shots';
const DB_VERSION = 1;
// Shots older than this are junk from an abandoned session — purged on open.
const MAX_AGE_MS = 48 * 60 * 60 * 1000;

function idbAvailable() {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
}

function openDB() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const d = req.result;
            if (!d.objectStoreNames.contains(STORE)) {
                const store = d.createObjectStore(STORE, { keyPath: 'id' });
                store.createIndex('clientId', 'clientId', { unique: false });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

function reqP(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

// Save/replace one shot: { id, clientId, blob, tags, createdAt }
export async function stashShot(shot) {
    if (!idbAvailable()) return;
    try {
        const d = await openDB();
        try { await reqP(d.transaction(STORE, 'readwrite').objectStore(STORE).put(shot)); }
        finally { d.close(); }
    } catch { /* stash is best-effort */ }
}

export async function updateShotTags(id, tags) {
    if (!idbAvailable()) return;
    try {
        const d = await openDB();
        try {
            const store = d.transaction(STORE, 'readwrite').objectStore(STORE);
            const existing = await reqP(store.get(id));
            if (existing) await reqP(store.put({ ...existing, tags }));
        } finally { d.close(); }
    } catch { /* ignore */ }
}

export async function removeShot(id) {
    if (!idbAvailable()) return;
    try {
        const d = await openDB();
        try { await reqP(d.transaction(STORE, 'readwrite').objectStore(STORE).delete(id)); }
        finally { d.close(); }
    } catch { /* ignore */ }
}

export async function clearShots(ids) {
    if (!idbAvailable()) return;
    try {
        const d = await openDB();
        try {
            const store = d.transaction(STORE, 'readwrite').objectStore(STORE);
            for (const id of ids) await reqP(store.delete(id));
        } finally { d.close(); }
    } catch { /* ignore */ }
}

// All stashed shots for a client (oldest first) — also purges stale rows
// (any client) while it's in there.
export async function loadShots(clientId) {
    if (!idbAvailable()) return [];
    try {
        const d = await openDB();
        try {
            const store = d.transaction(STORE, 'readwrite').objectStore(STORE);
            const all = (await reqP(store.getAll())) || [];
            const cutoff = Date.now() - MAX_AGE_MS;
            const keep = [];
            for (const s of all) {
                if ((s.createdAt || 0) < cutoff) { await reqP(store.delete(s.id)); continue; }
                if (s.clientId === clientId) keep.push(s);
            }
            keep.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
            return keep;
        } finally { d.close(); }
    } catch {
        return [];
    }
}
